import axios from "axios";
import { toast } from "react-toastify";
import { serviceGroupOf } from "./risIntegration";

// A normal OPD invoice (services picked by hand, not pulled in from the Staging
// Area) has no PatientLabTest row for its Laboratory / Radiology lines, so the
// phlebotomist and radiologist boards never see them. The Staging Area ones
// already have one, created by the doctor.
//
// Rows are created as NEW and tied to the invoice: get_patient_lab_tests/ hides
// OPD orders in NEW, and payment_details/ moves an invoice's tests to PAID when
// it is paid — from New Invoice, Pay Later → Pay Now, or any other screen.
// The backend stamps LAB-/RAD- order numbers from the service group itself.

const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL || "";
const DIAGNOSTIC_GROUPS = ["LABORATORY", "RADIOLOGY"];

// The patient's newest care context ("REF-…"), or "" when they have none.
// The backend lists them newest first, under this misspelt key.
async function fetchLatestCareContext(patientId, headers) {
  const res = await axios.post(
    `${REACT_APP_BACKEND_ROUTE_URL}fetch_care_contexts_by_filter/`,
    { filter_type: "patient_id", filter_details: { patient_id: patientId } },
    { headers },
  );
  const list = res?.data?.care_contenxts;
  return (Array.isArray(list) && list[0]?.care_context_reference) || "";
}

/**
 * Creates NEW lab orders for the Laboratory / Radiology services of a just
 * created OPD invoice, under the patient's newest care context. Never throws —
 * warns and resolves to [] instead, so the invoice flow is unaffected.
 *
 * @param {object} args
 * @param {string} args.patientId   UHID (PatientRecord.patient_id)
 * @param {Array}  args.services    get_medical_services/ rows of the invoice lines, any group
 * @param {string} args.invoiceNumber the invoice the tests are billed on
 * @param {string} args.doctorName  referring doctor, if any
 * @param {object} args.headers     request headers incl. Authorization
 * @returns {Promise<string[]>} lab_test_ids of the rows created
 */
export async function createOpdLabTestOrders({
  patientId,
  services,
  invoiceNumber,
  doctorName,
  headers,
}) {
  const tests = services.filter((svc) => DIAGNOSTIC_GROUPS.includes(serviceGroupOf(svc)));
  if (!patientId || !tests.length) return [];

  try {
    const careContextId = await fetchLatestCareContext(patientId, headers);
    if (!careContextId) {
      toast.warn("No visit found for this patient, so the tests were not sent to the lab / radiology dashboard.");
      return [];
    }

    const res = await axios.post(
      `${REACT_APP_BACKEND_ROUTE_URL}add_patient_lab_tests/`,
      {
        care_context_id: careContextId,
        patient_type: "OPD",
        status: "NEW",
        priority: "Routine",
        ...(invoiceNumber ? { invoice_number: invoiceNumber } : {}),
        ...(doctorName ? { doctor_name: doctorName } : {}),
        test: tests.map((svc) => ({
          name: svc.name || "",
          medical_service_id: String(svc.medical_service_id),
          ...(svc.snomed_code ? { code: svc.snomed_code } : {}),
        })),
      },
      { headers },
    );
    if (res?.data?.status !== "Success") throw new Error(res?.data?.message);
    return Array.isArray(res.data.lab_test_ids) ? res.data.lab_test_ids : [];
  } catch (err) {
    console.error("Could not create lab test orders:", err);
    toast.warn("Tests could not be sent to the lab / radiology dashboard.");
    return [];
  }
}
